import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { type SettingsProject, toSettingsProject } from "@/lib/projects/types";
import stores from "@/lib/stores/web-api-stores";
import type { TimeProjectRef, TimeTagRef } from "@/lib/timer/types";
import { toTimeProjectRef } from "@/lib/timer/utils";

export const projectsQueryKey = ["projects"] as const;

export const projectQueryKey = (id: string) => ["projects", id] as const;

/** Full project list (includes completed for settings archive filters). Consumers select/filter. */
export async function fetchProjects(): Promise<readonly SettingsProject[]> {
	const entities = await stores.projects.actions.fetchAll(true);
	const projects: SettingsProject[] = [];
	for (const entity of entities) {
		const project = toSettingsProject(entity as Record<string, unknown>);
		if (project) {
			projects.push(project);
		}
	}
	return projects.toSorted((a, b) => a.name.localeCompare(b.name));
}

export async function fetchProject(id: string): Promise<SettingsProject> {
	const entity = await stores.projects.actions.fetchById(id);
	const project = toSettingsProject(entity as Record<string, unknown>);
	if (!project) {
		throw new Error("project.not-found");
	}
	return project;
}

export type ProjectsQueryType = "project" | "template" | "all";

export function useProjects(options?: { enabled?: boolean; type?: ProjectsQueryType }) {
	const type = options?.type ?? "project";
	return useQuery({
		queryKey: projectsQueryKey,
		queryFn: fetchProjects,
		enabled: options?.enabled ?? true,
		select: (data) => {
			if (type === "all") {
				return data;
			}
			return data.filter((project) => project.projectType === type);
		},
	});
}

export function useProject(id: string, options?: { enabled?: boolean }) {
	return useQuery({
		queryKey: projectQueryKey(id),
		queryFn: () => fetchProject(id),
		enabled: id.length > 0 && (options?.enabled ?? true),
	});
}

export function useProjectMetaMap(): ReadonlyMap<string, TimeProjectRef> {
	const { data } = useProjects();
	return useMemo(() => {
		const map = new Map<string, TimeProjectRef>();
		for (const project of data ?? []) {
			const ref = toTimeProjectRef(project);
			if (ref) {
				map.set(ref.id, ref);
			}
		}
		return map;
	}, [data]);
}

export function useProjectMeta(projectId: string | null | undefined): TimeProjectRef | null {
	const map = useProjectMetaMap();
	if (!projectId) {
		return null;
	}
	return map.get(projectId) ?? null;
}

const NO_PROJECT_TAGS: readonly TimeTagRef[] = [];

/** Tags the project brings along; they are display only and never part of a time entry's tags. */
export function useProjectTags(projectId: string | null | undefined): readonly TimeTagRef[] {
	const { data } = useProjects();
	return useMemo(
		() => (projectId ? (data?.find((project) => project.id === projectId)?.tags ?? NO_PROJECT_TAGS) : NO_PROJECT_TAGS),
		[data, projectId]
	);
}
